const channels = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const lum = (hex) => {
  const [r, g, b] = channels(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

const scaleCss = (hex, f) => {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  const [r, g, b] = channels(hex);
  return `rgb(${c(r)}, ${c(g)}, ${c(b)})`;
};

const load = (src) =>
  new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

const tryLoad = (src) => (src ? load(src).catch(() => null) : Promise.resolve(null));

export async function composeBuildImage({ vehicle, color, finish, accents = [], overlayText = "" }) {
  const W = 1536;
  const H = 864;
  const [base, mask, hood, roof, trim] = await Promise.all([
    load(vehicle.img),
    tryLoad(vehicle.mask),
    tryLoad(vehicle.hoodMask),
    tryLoad(vehicle.roofMask),
    tryLoad(vehicle.trimMask),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.filter = finish === "matte" ? "contrast(0.9) brightness(1.06)" : "none";
  ctx.drawImage(base, 0, 0, W, H);
  ctx.filter = "none";

  const maskedLayer = (fill, comp, alpha, maskImg) => {
    if (!maskImg) return;
    const off = document.createElement("canvas");
    off.width = W;
    off.height = H;
    const o = off.getContext("2d");
    o.fillStyle = fill;
    o.fillRect(0, 0, W, H);
    o.globalCompositeOperation = "destination-in";
    o.drawImage(maskImg, 0, 0, W, H);
    ctx.save();
    ctx.globalCompositeOperation = comp;
    ctx.globalAlpha = alpha;
    ctx.drawImage(off, 0, 0);
    ctx.restore();
  };

  maskedLayer(scaleCss(color.hex, 1.38), "multiply", 1, mask);
  maskedLayer(color.hex, "color", 0.55, mask);

  if (accents.includes("chrome_delete")) maskedLayer("#0B0C0F", "source-over", 0.9, trim);
  if (accents.includes("roof_pillars")) maskedLayer("#0B0C0F", "source-over", 0.97, roof);
  if (accents.includes("hood_blackout") && hood) {
    maskedLayer("#101114", "source-over", 0.97, hood);
    const off = document.createElement("canvas");
    off.width = W;
    off.height = H;
    const o = off.getContext("2d");
    o.strokeStyle = "rgba(0,0,0,0.4)";
    o.lineWidth = 2;
    for (let x = -H; x < W; x += 5) {
      o.beginPath();
      o.moveTo(x, 0);
      o.lineTo(x + H, H);
      o.stroke();
    }
    o.globalCompositeOperation = "destination-in";
    o.drawImage(hood, 0, 0, W, H);
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.drawImage(off, 0, 0);
    ctx.restore();
  }

  if (accents.includes("calipers_red") && vehicle.calipers) {
    for (const c of vehicle.calipers) {
      const cx = (c.x / 100) * W;
      const cy = (c.y / 100) * H;
      const r = ((c.size / 100) * W) / 2;
      const g = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r);
      g.addColorStop(0, "rgba(255,42,59,0)");
      g.addColorStop(0.5, "rgba(255,42,59,0.95)");
      g.addColorStop(0.62, "rgba(176,14,30,0.9)");
      g.addColorStop(0.72, "rgba(255,42,59,0)");
      ctx.save();
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  if (mask) {
    const off = document.createElement("canvas");
    off.width = W;
    off.height = H;
    const o = off.getContext("2d");
    const ang = (112 * Math.PI) / 180;
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    const L = W * 0.8;
    const g = o.createLinearGradient(W / 2 - dx * L, H / 2 - dy * L, W / 2 + dx * L, H / 2 + dy * L);
    g.addColorStop(0.32, "rgba(255,255,255,0)");
    g.addColorStop(0.45, "rgba(255,255,255,1)");
    g.addColorStop(0.5, "rgba(255,255,255,0.35)");
    g.addColorStop(0.62, "rgba(255,255,255,0)");
    o.fillStyle = g;
    o.fillRect(0, 0, W, H);
    o.globalCompositeOperation = "destination-in";
    o.drawImage(mask, 0, 0, W, H);
    const sheenBase = { matte: 0.03, satin: 0.16, gloss: 0.46 }[finish] ?? 0.2;
    const darkBoost = lum(color.hex) < 0.15 ? 0.75 : 1;
    const alpha = Math.min(0.8, (sheenBase + (accents.includes("ceramic_over_wrap") ? 0.18 : 0)) * darkBoost);
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.globalAlpha = alpha;
    ctx.drawImage(off, 0, 0);
    ctx.restore();
  }

  if (overlayText) {
    try {
      await document.fonts.load('italic 900 100px "Cabinet Grotesk"');
    } catch (e) {}
    const x = (vehicle.decalZone.x / 100) * W;
    const y = (vehicle.decalZone.y / 100) * H;
    const size = Math.round(W * 0.075);
    const textColor = lum(color.hex) > 0.62 ? "#101216" : "#F8FAFC";
    ctx.save();
    ctx.translate(x, y);
    ctx.transform(1, 0, -0.14, 1, 0, 0);
    ctx.font = `italic 900 ${size}px "Cabinet Grotesk", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.globalAlpha = 0.94;
    ctx.fillStyle = textColor;
    ctx.shadowColor = textColor === "#F8FAFC" ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.25)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
    ctx.fillText(overlayText, 0, 0);
    ctx.restore();
  }

  return canvas;
}

const FONT_MAP = {
  speed: (s) => `italic 900 ${s}px "Cabinet Grotesk", sans-serif`,
  mono: (s) => `700 ${s}px "JetBrains Mono", monospace`,
  heavy: (s) => `900 ${s}px "Cabinet Grotesk", sans-serif`,
  stencil: (s) => `700 ${s}px "IBM Plex Sans", sans-serif`,
};

export async function composeDecalImage({ shapeName, ratio, text, fontId, colorHex, widthIn, heightIn }) {
  const W = 1280;
  const H = 720;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#1A212C");
  bg.addColorStop(0.5, "#0D1117");
  bg.addColorStop(1, "#05070A");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(142,149,165,0.07)";
  ctx.lineWidth = 1;
  for (let x = 0; x <= W; x += 36) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = 0; y <= H; y += 36) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.roundRect(48, 48, W - 96, H - 96, 18);
  ctx.fillStyle = "rgba(10,12,16,0.55)";
  ctx.fill();
  ctx.strokeStyle = "#252A36";
  ctx.stroke();

  const boxW = Math.min(W * 0.66, H * 0.52 * ratio);
  const boxH = boxW / ratio;
  const bx = (W - boxW) / 2;
  const by = (H - boxH) / 2 - 10;

  ctx.setLineDash([8, 6]);
  ctx.strokeStyle = "rgba(142,149,165,0.6)";
  ctx.strokeRect(bx, by, boxW, boxH);
  ctx.setLineDash([]);

  ctx.strokeStyle = "rgba(255,42,59,0.7)";
  ctx.fillStyle = "#FF2A3B";
  ctx.font = '600 22px "JetBrains Mono", monospace';
  ctx.textAlign = "center";
  ctx.beginPath();
  ctx.moveTo(bx, by - 26);
  ctx.lineTo(bx + boxW, by - 26);
  ctx.stroke();
  ctx.fillText(`${widthIn}"`, W / 2, by - 38);
  ctx.beginPath();
  ctx.moveTo(bx - 26, by);
  ctx.lineTo(bx - 26, by + boxH);
  ctx.stroke();
  ctx.save();
  ctx.translate(bx - 40, by + boxH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(`${heightIn}"`, 0, 0);
  ctx.restore();

  const label = fontId === "heavy" || fontId === "stencil" ? text.toUpperCase() : text;
  const fontStr = FONT_MAP[fontId] || FONT_MAP.speed;
  try {
    await document.fonts.load(fontStr(40));
  } catch (e) {}
  try {
    ctx.letterSpacing = fontId === "mono" ? "6px" : fontId === "stencil" ? "12px" : "0px";
  } catch (e) {}

  let size = boxH * 0.62;
  for (;;) {
    ctx.font = fontStr(Math.round(size));
    if (ctx.measureText(label).width <= boxW * 0.88 || size < 14) break;
    size *= 0.92;
  }
  ctx.textBaseline = "middle";
  ctx.shadowColor = `${colorHex}55`;
  ctx.shadowBlur = 18;
  ctx.fillStyle = colorHex;
  ctx.fillText(label, W / 2, by + boxH / 2);
  ctx.shadowBlur = 0;
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {}

  ctx.font = '600 20px "JetBrains Mono", monospace';
  ctx.fillStyle = "#8E95A5";
  ctx.fillText(`${shapeName} — ${widthIn}" × ${heightIn}"`, W / 2, H - 84);

  return canvas;
}

export async function composeTintImage({ vehicle, level }) {
  const W = 1280;
  const H = 720;
  const [base, glass] = await Promise.all([load(vehicle.img), tryLoad(vehicle.glassMask)]);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(base, 0, 0, W, H);
  if (glass) {
    const mkLayer = (fill) => {
      const off = document.createElement("canvas");
      off.width = W;
      off.height = H;
      const o = off.getContext("2d");
      o.fillStyle = fill;
      o.fillRect(0, 0, W, H);
      o.globalCompositeOperation = "destination-in";
      o.drawImage(glass, 0, 0, W, H);
      return off;
    };
    const light = mkLayer("#8C9BAC");
    ctx.save();
    ctx.globalAlpha = level.light;
    ctx.drawImage(light, 0, 0);
    ctx.restore();
    const dark = mkLayer("#04060B");
    ctx.save();
    ctx.globalAlpha = level.alpha;
    ctx.drawImage(dark, 0, 0);
    ctx.restore();
  }
  ctx.font = '600 22px "JetBrains Mono", monospace';
  ctx.fillStyle = "rgba(142,149,165,0.9)";
  ctx.textAlign = "left";
  ctx.fillText(`NANO-CERAMIC · VLT ${level.vlt}% · ${level.label.toUpperCase()}`, 36, H - 36);
  return canvas;
}

